import React, { useEffect, useState } from "react";
import { useMemo } from "react";
import styles from "./styles.module.scss";
import { MultiSelect, PhaseSelect, SelectOption } from "./Select";
import { ScrollContainer } from "../scrollContainer";
import { BetterPopover } from "../popover";
import { uniqueId } from "lodash";
import {
  Button,
  NativeSelect,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
} from "@altimateai/lego";
import classNames from "classnames";
import { DebouncedInput } from "./Input";
import { ComponentProps } from "react";
import { IconFilter2 } from "@tabler/icons-react";

interface DropdownProps {
  children?: React.ReactNode;
  options: SelectOption[];
  onChange?: (value: string) => void;
  id?: string;
  value?: string | ((value: string) => boolean);
  isMultiSelect?: boolean;
  popoverContentProps?: ComponentProps<typeof PopoverContent>;
  label?: string;
  showDivider?: boolean;
  wrapperClass?: string;
  maxOptionLimit?: number;
}

const Dropdown = ({
  children,
  options,
  onChange,
  id,
  value,
  isMultiSelect = false,
  popoverContentProps = {},
  label = undefined,
  showDivider,
  wrapperClass = "",
  maxOptionLimit = 50,
}: DropdownProps) => {
  if (!isMultiSelect) {
    return (
      <NativeSelect
        options={options}
        onChange={onChange}
        size={"sm"}
        value={typeof value === "string" ? value : undefined}
        icon={<IconFilter2 className="al-size-4" />}
        buttonProps={{
          className: "!al-h-4 !al-w-4 al-border-0 !al-p-0 [&_.tabler-icon-chevron-down]:al-hidden",
        }}
      />
    );
  }

  return (
    <BetterPopover
      id={`popover-${id}`}
      popoverContentProps={popoverContentProps}
      trigger={<div className={classNames("cursor-pointer", wrapperClass)}>{children}</div>}
      render={() => (
        <div className={styles.dropdown_container}>
          {label && (
            <>
              <div className="fs-5 fw-semibold m-2">{label}</div>
              <div className={styles.divider} />
            </>
          )}
          <ScrollContainer>
            {isMultiSelect ? (
              <MultiSelect
                options={options}
                onChange={onChange}
                value={typeof value === "function" ? value : () => false}
                showDivider={showDivider}
                maxOptionLimit={maxOptionLimit}
              />
            ) : null}
          </ScrollContainer>
        </div>
      )}
    />
  );
};

interface AsyncMultiSelectDropdownProps {
  children?: React.ReactNode;
  options: SelectOption[];
  onChange: (value: string) => void;
  value: (value: string) => boolean;
  onSearch?: () => void;
  onApply: () => void;
  disabled?: boolean;
}

const AsyncMultiSelectDropdown = ({
  children,
  options,
  onChange,
  value,
  onSearch,
  onApply,
  disabled = false,
}: AsyncMultiSelectDropdownProps) => {
  const id = useMemo(() => uniqueId("async-multi-select-dropdown-"), []);
  const isSearch = typeof onSearch === "function";
  return (
    <BetterPopover
      id={`popover-${id}`}
      trigger={<div className="al-w-full cursor-pointer">{children}</div>}
      render={({ close }) => (
        <div className={classNames(styles.dropdown_container, styles.async_container)}>
          {isSearch && (
            <DebouncedInput
              size="sm"
              placeholder="Search"
              onChange={onSearch}
              aria-label="Search"
            />
          )}
          <ScrollContainer>
            <MultiSelect options={options} onChange={onChange} value={value} showDivider={false} />
          </ScrollContainer>
          <div className={styles.divider} />
          <Stack className="m-2 gap-sm" justify="end">
            <Button
              variant="ghost"
              className={styles.cancel_button}
              onClick={e => {
                e.stopPropagation();
                close();
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={disabled}
              onClick={e => {
                e.stopPropagation();
                onApply();
                close();
              }}
            >
              Apply
            </Button>
          </Stack>
        </div>
      )}
    />
  );
};

interface ChildPhaseProps {
  parentClose: () => void;
  hoveredOption: SelectOption;
  setHoveredOption: (option: SelectOption | null) => void;
  placement?: "left" | "right" | "top" | "bottom";
}

const ChildPhase = ({
  parentClose,
  hoveredOption,
  setHoveredOption,
  placement,
}: ChildPhaseProps) => {
  useEffect(() => {
    return () => {
      setHoveredOption(null);
    };
  }, []);

  return (
    <Popover open={true}>
      <PopoverTrigger asChild>
        <div id={hoveredOption.value} className={classNames("cursor-pointer")}></div>
      </PopoverTrigger>
      <PopoverContent side={placement || "left"}>
        {hoveredOption.subComponent?.(parentClose)}
      </PopoverContent>
    </Popover>
  );
};

interface MultiPhaseDropdownProps {
  children?: React.ReactNode;
  options: SelectOption[];
  id?: string;
  label?: string;
  showDivider?: boolean;
  wrapperClass?: string;
  optionStyles?: React.CSSProperties;
  placement?: "left" | "right" | "top" | "bottom";
  openMultiselectOnHover?: boolean;
  openMultiselectOnFirstHover?: boolean;
}

const MultiPhaseDropdown = ({
  children,
  options,
  id,
  label,
  showDivider,
  wrapperClass = "",
  optionStyles = {},
  placement,
  openMultiselectOnHover = true,
  openMultiselectOnFirstHover = false,
}: MultiPhaseDropdownProps) => {
  const [hoveredOption, setHoveredOption] = useState<SelectOption | null>(null);
  const handleOptionHoverOrClick = (e: React.MouseEvent<HTMLLIElement>) => {
    const option = options.find(x => x.value === (e.target as HTMLElement).id);
    if (option) {
      setHoveredOption(option);
    }
  };

  const elem = document.getElementById(hoveredOption?.value ?? "");
  useEffect(() => {
    if (hoveredOption && !elem) {
      setHoveredOption(null);
    }
  }, [elem, hoveredOption]);

  return (
    <BetterPopover
      id={`popover-${id}`}
      trigger={<div className={classNames("cursor-pointer", wrapperClass)}>{children}</div>}
      render={({ close }) => (
        <div className={styles.dropdown_container}>
          {label && (
            <>
              <div className="fs-5 fw-semibold m-2">{label}</div>
              <div className={styles.divider} />
            </>
          )}
          <ScrollContainer>
            <PhaseSelect
              options={options}
              optionStyles={optionStyles}
              showDivider={showDivider}
              handleOptionHover={handleOptionHoverOrClick}
              handleOptionClick={handleOptionHoverOrClick}
              openMultiselectOnHover={openMultiselectOnHover}
              openMultiselectOnFirstHover={openMultiselectOnFirstHover}
            />
          </ScrollContainer>
          {hoveredOption && hoveredOption.subComponent && (
            <ChildPhase
              parentClose={close}
              hoveredOption={hoveredOption}
              setHoveredOption={setHoveredOption}
              placement={placement}
            />
          )}
        </div>
      )}
    />
  );
};

export { Dropdown, AsyncMultiSelectDropdown, MultiPhaseDropdown };
